import React, { useState, useEffect } from 'react';
import { sessions, functions } from '../api';
import { 
  Zap, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  FlaskConical, 
  Code, 
  Users, 
  Activity,
  Send,
  Loader2,
  Sparkles
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

const ApiInjection = () => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [injectionData, setInjectionData] = useState({
    function_name: '',
    type: 'tool_result',
    parameters: {}
  });
  const [parameterInput, setParameterInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const injectionTypes = [
    { 
      value: 'tool_result', 
      label: 'Tool Result', 
      description: 'Inject API call results',
      icon: Code,
      color: 'blue',
      gradient: 'from-blue-500/10 to-blue-600/5',
      borderColor: 'border-blue-500/30',
      textColor: 'text-blue-400'
    },
    { 
      value: 'context_update', 
      label: 'Context Update', 
      description: 'Update conversation context',
      icon: Sparkles,
      color: 'purple',
      gradient: 'from-purple-500/10 to-purple-600/5',
      borderColor: 'border-purple-500/30',
      textColor: 'text-purple-400'
    },
    { 
      value: 'prompt_injection', 
      label: 'Prompt Injection', 
      description: 'Inject prompts into AI',
      icon: Zap,
      color: 'red',
      gradient: 'from-red-500/10 to-red-600/5',
      borderColor: 'border-red-500/30',
      textColor: 'text-red-400'
    },
    { 
      value: 'user_data', 
      label: 'User Data', 
      description: 'Inject user-specific data',
      icon: Users,
      color: 'green',
      gradient: 'from-green-500/10 to-green-600/5',
      borderColor: 'border-green-500/30',
      textColor: 'text-green-400'
    }
  ];

  useEffect(() => {
    loadData();
    const interval = setInterval(loadActiveSessions, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await Promise.all([
      loadActiveSessions(),
      loadFunctions()
    ]);
  };

  const loadActiveSessions = async () => {
    try {
      setRefreshing(true);
      const response = await sessions.list(1, 100);
      const active = response.data.sessions.filter(session => session.status === 'ACTIVE');
      setActiveSessions(active);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const loadFunctions = async () => {
    try {
      const response = await functions.list();
      setAvailableFunctions(response.data.functions || []);
    } catch (err) {
      console.error('Failed to load functions:', err);
    }
  };

  const handleInject = async () => {
    if (!selectedSession || !injectionData.function_name) {
      setError('Please select a session and function');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      // Parse parameters from JSON input
      let parameters = {};
      if (parameterInput.trim()) {
        try {
          parameters = JSON.parse(parameterInput);
        } catch (e) {
          setError('Invalid JSON in parameters field');
          return;
        }
      }

      const payload = {
        ...injectionData,
        parameters
      };

      const response = await sessions.injectApi(selectedSession, payload);
      setMessage(`API injection successful! Injection ID: ${response.data.injection_id}`);
      
      // Clear form after a delay
      setTimeout(() => {
        setParameterInput('');
        setInjectionData({
          function_name: '',
          type: 'tool_result',
          parameters: {}
        });
        setMessage('');
      }, 3000);

    } catch (err) {
      setError('Failed to inject API call: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFunctionSelect = (functionName) => {
    setInjectionData({ ...injectionData, function_name: functionName });
    
    // Auto-populate example parameters based on function
    const selectedFunction = availableFunctions.find(f => f.name === functionName);
    if (selectedFunction) {
      const exampleParams = {};
      Object.entries(selectedFunction.parameters || {}).forEach(([key, config]) => {
        if (config.default !== undefined) {
          exampleParams[key] = config.default;
        } else if (config.type === 'string') {
          exampleParams[key] = config.required ? `example_${key}` : '';
        } else if (config.type === 'boolean') {
          exampleParams[key] = false;
        } else {
          exampleParams[key] = '';
        }
      });
      setParameterInput(JSON.stringify(exampleParams, null, 2));
    }
  };

  const formatSessionDisplay = (session) => {
    const duration = session.duration ? `${Math.round(session.duration / 60)}m` : 'Active';
    const channel = session.channel === 'phone' ? '📞' : '🌐';
    return `${channel} ${session.agent_name} - ${session.room} (${duration})`;
  };

  const handleQuickAction = (type) => {
    const typeConfig = injectionTypes.find(t => t.value === type);
    if (!typeConfig) return;

    switch (type) {
      case 'tool_result':
        setInjectionData({ function_name: 'check_account_balance', type: 'tool_result', parameters: {} });
        setParameterInput(JSON.stringify({
          account_number: "****7892",
          current_balance: "$12,458.73",
          available_balance: "$12,258.73",
          pending_transactions: [{
            description: "Pending Deposit - Salary",
            amount: "+$3,200.00"
          }],
          last_updated: "2025-09-06 10:30 AM"
        }, null, 2));
        break;
      case 'context_update':
        setInjectionData({ function_name: 'customer_context', type: 'context_update', parameters: {} });
        setParameterInput(JSON.stringify({
          customer_tier: "premium",
          priority: "high",
          membership_level: "gold",
          support_language: "english"
        }, null, 2));
        break;
      case 'prompt_injection':
        setInjectionData({ function_name: 'emergency_prompt', type: 'prompt_injection', parameters: {} });
        setParameterInput(JSON.stringify({
          prompt: "The customer just mentioned they are having a financial emergency and need immediate assistance. Be extremely empathetic, offer priority support, and guide them to emergency services if needed."
        }, null, 2));
        break;
      case 'user_data':
        setInjectionData({ function_name: 'user_profile', type: 'user_data', parameters: {} });
        setParameterInput(JSON.stringify({
          user_name: "Sarah Johnson",
          account_type: "Business Premium",
          location: "New York",
          preferred_contact: "email",
          last_transaction: "Wire transfer $50,000",
          account_since: "2018"
        }, null, 2));
        break;
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
              <FlaskConical className="w-8 h-8 text-primary" />
              Real-Time API Injection
            </h1>
            <p className="text-muted-foreground">Inject data and instructions into active agent sessions in real-time</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-card/80 border border-border/60 rounded-lg">
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-sm text-foreground font-medium">
                {activeSessions.length} Active
              </span>
            </div>
            <button
              onClick={loadActiveSessions}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-card/80 border border-border/60 rounded-lg text-foreground hover:bg-card hover:border-border transition-all duration-300 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-success" />
          <p className="text-sm text-success">{message}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Injection Form */}
          <div className="rounded-lg border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Injection Configuration
            </h3>
            
            <div className="space-y-5">
              {/* Session Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Target Session
                </label>
                <select
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                >
                  <option value="">Select an active session</option>
                  {activeSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {formatSessionDisplay(session)}
                    </option>
                  ))}
                </select>
                {activeSessions.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    No active sessions found. Start a call to inject data.
                  </p>
                )}
              </div>

              {/* Injection Type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Injection Type
                </label>
                <select
                  value={injectionData.type}
                  onChange={(e) => setInjectionData({ ...injectionData, type: e.target.value })}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                >
                  {injectionTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Function Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Function Name
                </label>
                <input
                  type="text"
                  value={injectionData.function_name}
                  onChange={(e) => setInjectionData({ ...injectionData, function_name: e.target.value })}
                  placeholder="Enter any function name (e.g., check_account_balance, transfer_funds)"
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>

              {/* Parameters */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Parameters (JSON)
                </label>
                <textarea
                  value={parameterInput}
                  onChange={(e) => setParameterInput(e.target.value)}
                  placeholder='{"key": "value", "param": "data"}'
                  rows={8}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm transition-all resize-none"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Enter parameters as valid JSON. Use the quick actions below to populate examples.
                </p>
              </div>

              {/* Inject Button */}
              <button
                onClick={handleInject}
                disabled={loading || !selectedSession || !injectionData.function_name}
                className="w-full px-4 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 btn-hover-gradient disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Injecting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Inject API Call
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Test Injection Types */}
          <div className="rounded-lg border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Quick Actions
            </h3>
            
            <div className="space-y-4">
              {injectionTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <div
                    key={type.value}
                    onClick={() => handleQuickAction(type.value)}
                    className={`p-4 bg-gradient-to-br ${type.gradient} border ${type.borderColor} rounded-lg cursor-pointer hover:scale-[1.02] transition-all duration-300 group`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${type.textColor}`} />
                        <h4 className="font-semibold text-foreground">{type.label}</h4>
                      </div>
                      <span className={`text-xs ${type.textColor} px-2 py-1 rounded ${
                        type.color === 'blue' ? 'bg-blue-500/20' :
                        type.color === 'purple' ? 'bg-purple-500/20' :
                        type.color === 'red' ? 'bg-red-500/20' :
                        'bg-green-500/20'
                      }`}>
                        {type.value === 'tool_result' ? 'API Results' : 
                         type.value === 'context_update' ? 'Variables' :
                         type.value === 'prompt_injection' ? 'Immediate' : 'Profile'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{type.description}</p>
                    <div className="text-xs text-muted-foreground">
                      <p>
                        <span className={type.textColor}>Effect: </span>
                        {type.value === 'tool_result' && 'Agent acknowledges function completion and provides results to customer'}
                        {type.value === 'context_update' && 'Agent adapts service level and tone based on updated context'}
                        {type.value === 'prompt_injection' && 'Agent immediately adapts tone and approach for specific situations'}
                        {type.value === 'user_data' && 'Agent personalizes responses using customer-specific information'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiInjection;
